import { useCallback, useContext, useEffect, useState } from "react";
import qs from "qs";
import { CircularProgress, Grid, Typography } from "@material-ui/core";
import { useParams, useLocation, useHistory } from "react-router-dom";
import WrappedSidebarV2 from "v2/ui/components/SidebarV2";
import { SplitTreatments } from "@splitsoftware/splitio-react";
import featureFlagsDictionary from "infrastructure/split/feature-flags-dictionary";
import { BenefitCategory } from "v2/domain/common/entities/BenefitCagetory";
import { BreadCrumb, tokens } from "@cobee/cobee-ui";
import SectionContentWrapper from "../../../../components/SectionContentWrapper";
import useStyles from "./styles";
import useCommonStyles from "../../../../../infrastructure/styles";
import SearchField from "../../../../components/SearchField";
import RecurrentPaymentContext from "../../../../../infrastructure/Contexts/components/data/RecurrentPaymentsWrapper/context";
import ModalContext from "../../../../../infrastructure/Contexts/components/ModalWrapper/context";
import CompanyContext from "../../../../../infrastructure/Contexts/components/data/CompanyWrapper/context";
import RecurrentPaymentsFilters from "../RecurrentPaymentsFilters";
import InfiniteTable from "../../../../components/InfiniteTable";
import { ReactComponent as EmailIcon } from "../../../../../assets/icons/email.svg";
import { recurrentPaymentConfig } from "../../config";
import SendReportModal from "../../../../components/modals/recurrentPayments/SendReportModal";
import EmptyContentFilter from "../EmptyContentFilter";
import { translate as t } from "../../../../../infrastructure/translate/localization";

interface Params {
  type: string;
}

export default function RecurrentPaymentsTable(): JSX.Element {
  const { type } = useParams<Params>();
  const { search } = useLocation();
  const history = useHistory();
  const params = qs.parse(search, { ignoreQueryPrefix: true });

  const { company } = useContext(CompanyContext);
  const { id: companyId } = company;
  const {
    loadRecurrentPayments,
    recurrentPayments,
    setRecurrentPayments,
    isLoading,
    totalItems,
    initialLoad,
    setInitialLoad,
  } = useContext(RecurrentPaymentContext);
  const { openModal } = useContext(ModalContext);

  const { columns, parseData, hideDetails, hideEmail } =
    recurrentPaymentConfig[type] || recurrentPaymentConfig.default;

  const [pageNumber, setPageNumber] = useState<number>(1);

  const { downloadButtonContainer, donwloadLabel, containerLoader, backLabel } =
    useStyles({
      totalItems,
    });
  const { sidebarSpacer, container } = useCommonStyles({});

  const parseParams = useCallback(
    (newParams) => ({
      ...params,
      category: `${type}-benefit`,
      pageNumber,
      ...newParams,
    }),
    [params, type, pageNumber]
  );

  useEffect(() => {
    if (initialLoad) {
      loadRecurrentPayments({
        companyId,
        params: parseParams({}),
        type: "load",
      });
    }
  }, [loadRecurrentPayments, companyId, parseParams, initialLoad]);

  useEffect(() => {
    return () => {
      setInitialLoad(true);
      setRecurrentPayments([]);
    };
  }, [setInitialLoad, setRecurrentPayments]);

  const loadMoreData = () => {
    loadRecurrentPayments({
      companyId,
      params: parseParams({ pageNumber: pageNumber + 1 }),
      type: "loadMore",
    });
    setPageNumber((prevPage) => prevPage + 1);
  };

  const filterData = (filter) => {
    setPageNumber(1);
    const parsedParams = parseParams(filter);
    loadRecurrentPayments({
      companyId,
      params: parsedParams,
      type: "filter",
    });
    const replaceParams = { ...parsedParams, category: null, pageNumber: null };
    history.replace(
      `/benefits/recurrent-payments/${type}?${qs.stringify(replaceParams, {
        skipNulls: true,
      })}`
    );
  };

  const breadCrumbItems = [
    {
      url: "/v2/requests",
      displayName: t("v2.request-benefits.page.title"),
    },
    {
      url: `/v2/benefits/recurrent-payments/${type}`,
      displayName: t("recurrent-payments-section.payment-planning"),
    },
  ];

  return (
    <Grid item container xs={12} className={sidebarSpacer}>
      <WrappedSidebarV2 />

      <Grid item container xs={12} className={container}>
        <Grid item className={backLabel}>
          <BreadCrumb items={breadCrumbItems} />
        </Grid>

        <SectionContentWrapper
          title={t("recurrent-payments-section.payment-planning")}
          titlePadding="0px"
        >
          <SearchField
            action={(value) => {
              const textSearchValue = value === "" ? null : value;
              filterData({ textSearchValue, pageNumber: 1 });
            }}
          />

          <Grid container alignItems="center">
            <RecurrentPaymentsFilters
              type={type}
              handleChangeFilter={(filter) => filterData(filter)}
            />
            {!hideEmail && (
              <Grid
                container
                className={downloadButtonContainer}
                item
                xs={2}
                alignItems="center"
                justifyContent="flex-end"
                onClick={() =>
                  openModal({
                    title: "",
                    modalContents: (
                      <SendReportModal
                        category={`${type}-benefit`}
                        params={params}
                      />
                    ),
                  })
                }
              >
                <Typography className={donwloadLabel}>
                  {t("recurrent-payments-section.send-by-email")}
                  <EmailIcon
                    style={{
                      marginLeft: "8px",
                    }}
                    fill={tokens.palette.secondary.main}
                  />
                </Typography>
              </Grid>
            )}
          </Grid>

          {isLoading ? (
            <Grid
              item
              container
              xs={12}
              justifyContent="center"
              alignItems="center"
              className={containerLoader}
            >
              <CircularProgress color="secondary" />
            </Grid>
          ) : (
            <SplitTreatments
              names={[featureFlagsDictionary.ENT_259]}
              attributes={{
                companyId,
                country: company.countryISO2Code,
                benefit: BenefitCategory.RENTING,
              }}
            >
              {({ treatments, isReady }) => {
                const showNewRentinExperience =
                  treatments[featureFlagsDictionary.ENT_259].treatment === "on";

                return (
                  <InfiniteTable
                    columns={columns}
                    data={parseData(recurrentPayments, type)}
                    handleLoadMoreData={loadMoreData}
                    totalItems={totalItems}
                    hasPagination
                    emptyComponent={<EmptyContentFilter />}
                    onRowClick={
                      hideDetails
                        ? undefined
                        : ({ rowData: { id } }) => {
                            if (
                              type === BenefitCategory.RENTING &&
                              isReady &&
                              showNewRentinExperience
                            ) {
                              window.location.assign(
                                `/v2/benefits/${type}/request/${id}`
                              );
                            } else {
                              history.push(
                                `/benefits/recurrent-payments/${type}/${id}`
                              );
                            }
                          }
                    }
                  />
                );
              }}
            </SplitTreatments>
          )}
        </SectionContentWrapper>
      </Grid>
    </Grid>
  );
}
